import ApplicationController from './application_controller'
import { ClassToggler } from '../classes/classToggler'
import { useDebounce } from 'stimulus-use'

export default class extends ApplicationController {
  static targets = ['item', 'itemsVisibleCounter', 'indicator', 'itemsContainer']
  static debounces = ['updateIndicator']

  doConnect() {
    useDebounce(this)

    this._viewportObserver = new IntersectionObserver(entries => this.processViewportIntersections(entries))
    this._itemsContainerObserver = new IntersectionObserver(entries => this.processItemsContainerIntersections(entries), { root: this.itemsContainerTarget, threshold: [0, 1] })
    this._showOnlyFirstRowClassesToggler = new ClassToggler(this.itemsContainerTarget, this.showOnlyFirstRowClasses)
    this._elementBaseClassesToggler = new ClassToggler(this.itemsContainerTarget, this.baseClasses)
    this._lastVisibleItem = null
    this._showingAllRows = false
    this._initialSetupDone = false
    this._itemsContainerVisible = false
    this._updateIndicatorPending = true

    this._elementBaseClassesToggler.addAllClasses()
    this._viewportObserver.observe(this.itemsContainerTarget)

    if (this.hasIndicatorTarget) {
      this.indicatorTarget.addEventListener('click', this.toggleAllRows.bind(this))
    }
  }

  disconnect() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.removeEventListener('click', this.toggleAllRows.bind(this))
    }
  }

  get baseClasses() {
    return 'flex flex-row flex-wrap items-start box-content'
  }

  get showOnlyFirstRowClasses() {
    return 'overflow-hidden h-height-0'
  }

  processViewportIntersections(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (!this._initialSetupDone) {
          window.requestIdleCallback(() => this.initialSetup())
        }

        if (this._updateIndicatorPending) {
          window.requestIdleCallback(() => {
            this.updateIndicator()
          })
          this._updateIndicatorPending = false
        }

        this._itemsContainerVisible = true
      } else {
        this._itemsContainerVisible = false
      }
    })
  }

  processItemsContainerIntersections(entries) {
    if (entries && entries.length > 0) {
      this._updateIndicatorPending = true
      if (this._itemsContainerVisible) {
        window.requestIdleCallback(() => {
          this.updateIndicator()
          this._updateIndicatorPending = false
        })
      }
    }
  }

  initialSetup() {
    this.showOnlyFirstRow()
    this.updateIndicator()
    setTimeout(() => {
      this.itemTargets.forEach(item => this._itemsContainerObserver.observe(item))
      this._initialSetupDone = true
    }, 0)
  }

  toggleAllRows(event) {
    if (this._showingAllRows) {
      this.showOnlyFirstRow()
    } else if (this.indicatorTarget.innerHTML !== '') {
      this.showAllRows()
    }
  }

  showAllRows() {
    this._showingAllRows = true
    this._showOnlyFirstRowClassesToggler.removeAllClasses()

    // Cross browser support for removing inline style
    if (this.itemsContainerTarget.style.removeProperty) {
      this.itemsContainerTarget.style.removeProperty('height')
    } else {
      this.itemsContainerTarget.style.removeAttribute('height')
    }
  }

  showOnlyFirstRow() {
    this._showingAllRows = false
    this._showOnlyFirstRowClassesToggler.addAllClasses()
    const firstRowBiggestItemHeight = this.itemTarget.offsetHeight
    this.itemsContainerTarget.style.height = `${firstRowBiggestItemHeight}px`
  }

  getAmountItemsFirstRow() {
    let previousItem = null
    let amountItemsVisible = 0
    this.itemTargets.forEach((item) => {
      if (previousItem) {
        const previousItemRect = previousItem.getBoundingClientRect()
        const currentItemRect = item.getBoundingClientRect()
        if (previousItemRect.left > currentItemRect.left || previousItemRect.top + previousItemRect.height < currentItemRect.top) {
          return true
        }
      }
      amountItemsVisible += 1
      previousItem = item
    })
    return amountItemsVisible
  }

  updateIndicator() {
    if (this._showingAllRows) {
      this.indicatorTarget.innerHTML = 'Show less'
    } else {
      const amountUnseenItems = this.itemTargets.length - this.getAmountItemsFirstRow()
      if (!this.hasIndicatorTarget) return
      this.indicatorTarget.innerHTML = amountUnseenItems === 0 ? '' : `+${amountUnseenItems} more`
    }
  }
}
